import { useReactFlow } from '@xyflow/react';
import { memo, ReactNode, useCallback, useMemo, useState } from 'react';
import { MenuTrigger } from 'react-aria-components';

import { Button, Flex, Menu, MenuItem, MenuItemDelete, Popover, Save, Text } from '@/components/core';
import { ExportOption, NewExportModal } from '@/components/flow';
import { AddIcon, OverflowIcon } from '@/components/icons';
import { useAddExport } from '@/hooks';
import { toAgentId, useDeleteAction, useSuspenseGetAgent, useUpdateAgent } from '@/hooks/api';

export type ExportHeaderProps = {
  agentId: string;
  actionId: string;
  children?: ReactNode;
};

const IExportHeader = ({ agentId, actionId, children }: ExportHeaderProps) => {
  const [newExportModelOpened, setNewExportModalOpened] = useState(false);
  const { data: agent } = useSuspenseGetAgent(toAgentId(agentId));
  const builder = useReactFlow();
  const onSuccess = useCallback(() => {
    builder.setNodes((nodes) => nodes);
    setNewExportModalOpened(false);
  }, [builder]);
  const { addExport, isPending: isAddingExport } = useAddExport({ onSuccess });

  const { mutate: updateAgent, isPending: isPendingUpdateAgent } = useUpdateAgent();
  const { mutate: deleteAction, isPending: isPendingDeleteAction } = useDeleteAction();

  const deleteExport = useCallback(() => {
    const updates = { resourceIds: agent.resourceIds.filter((r) => ![actionId].includes(r)) };
    updateAgent(
      { id: toAgentId(agentId), updates },
      {
        onSuccess: () => {
          // TODO: Need to also delete hooks
          deleteAction(
            { actionId },
            {
              onSuccess: () => {
                // TODO: Avoid force-triggering rerender of flow
                builder.setNodes((nodes) => nodes);
              },
            }
          );
        },
      }
    );
  }, [agent.resourceIds, updateAgent, agentId, actionId, deleteAction, builder]);

  const onExportOptionSelected = useCallback(
    async (exportOption: ExportOption) => {
      await addExport({ agentId, exportOption });
    },
    [addExport, agentId]
  );

  const isDeleting = useMemo(
    () => isPendingDeleteAction || isPendingUpdateAgent,
    [isPendingDeleteAction, isPendingUpdateAgent]
  );

  // TODO: Figure out why uncontrolled doesn't work here
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Flex className="w-full">
      <Flex className="gap-sm grow items-center">{children}</Flex>
      {isDeleting ? (
        <Save message="Deleting" />
      ) : (
        <MenuTrigger>
          <Button variant="plain" onPress={() => setIsOpen(true)}>
            <OverflowIcon />
          </Button>
          <Popover isOpen={isOpen} onOpenChange={setIsOpen}>
            <Menu>
              <MenuItemDelete onAction={deleteExport} />
              <MenuItem
                onAction={() => {
                  setNewExportModalOpened(true);
                  setIsOpen(false);
                }}
              >
                <AddIcon />
                <Text>New export</Text>
              </MenuItem>
            </Menu>
          </Popover>
        </MenuTrigger>
      )}
      <NewExportModal
        isAddingExport={isAddingExport}
        isOpen={newExportModelOpened}
        onExportOptionSelected={onExportOptionSelected}
        onOpenChange={() => setNewExportModalOpened(false)}
      />
    </Flex>
  );
};

export const ExportHeader = memo(IExportHeader) as typeof IExportHeader;
